import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { FeatureCollection, GeoJSON } from 'geojson';
import { catchError, finalize, map, Observable, Subscription, throwError } from 'rxjs';

import { BYPASS_INTERCEPTOR } from '@connect-services/client/interceptors/http-errors.interceptor';
import { GmlConverterService } from '@connect-services/client/services/gml-converter.service';
import { Layer, LayerOptions } from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';
import WfsExtensionEnum = LayerOptions.WfsExtensionEnum;

@Injectable({
    providedIn: 'root',
})
export class WfsRequestService {
    public readonly isFetchingWfsData: WritableSignal<boolean> = signal(false);
    public ongoingRequests: string[] = [];
    public wfsSubscriptions: Subscription[] = [];

    private completedRequests = 0;
    private readonly gmlConverterService = inject(GmlConverterService);
    private readonly http = inject(HttpClient);
    private isRequestErrored = false;
    private readonly snackBarService: SnackBarService = inject(SnackBarService);
    private readonly WFS_REQUEST_CONTEXT = new HttpContext().set(BYPASS_INTERCEPTOR, [
        'httpAcceptLanguageInterceptor',
        'httpApplicationNameInterceptor',
    ]);

    public cancelAllWfsRequests(): void {
        if (!this.wfsSubscriptions.length) {
            return;
        }

        this.wfsSubscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
        this.resetSubscriptions();
    }

    public getWfsData(layer: Layer): Observable<GeoJSON> | undefined {
        const url = layer.url!;

        if (this.ongoingRequests.includes(url)) {
            return;
        }

        this.isFetchingWfsData.set(true);
        this.ongoingRequests.push(url);
        let request$: Observable<GeoJSON>;

        if (layer.options!.wfsExtension === WfsExtensionEnum.GML) {
            request$ = this.http
                .get(url, {
                    context: this.WFS_REQUEST_CONTEXT,
                    responseType: 'text',
                })
                .pipe(map((rawXml: string): FeatureCollection => this.gmlConverterService.convertGMLToGeoJSON(rawXml)));
        } else {
            request$ = this.http.get<GeoJSON>(url, { context: this.WFS_REQUEST_CONTEXT });
        }

        return request$.pipe(
            catchError((error: HttpErrorResponse) => {
                this.isRequestErrored = true;

                return throwError(() => error);
            }),
            finalize(() => this.finalizeWfsRequest()),
        );
    }

    private finalizeWfsRequest(): void {
        this.completedRequests += 1;

        if (this.isRequestErrored && this.completedRequests === this.wfsSubscriptions.length) {
            this.snackBarService.openErrorSnackBar('errorWhileFetchingWFS');
        }

        if (this.completedRequests === this.wfsSubscriptions.length) {
            this.resetSubscriptions();
        }
    }

    private resetSubscriptions(): void {
        this.isFetchingWfsData.set(false);
        this.ongoingRequests = [];
        this.completedRequests = 0;
        this.isRequestErrored = false;
        this.wfsSubscriptions = [];
    }
}
